import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';

import { useApiClient } from '@@common';
import { AuthContext } from '@@components';

import { ApiException, RegistrationClient } from '../../api/api';

const socialProviderIcons = {
	google: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/google/google-original.svg',
	facebook: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/facebook/facebook-original.svg',
};

const pageSx = {
	minHeight: '100vh',
	width: '100%',
	bgcolor: 'background.default',
	color: 'text.primary',
};

const BrandPanel = () => (
	<Box
		sx={{
			display: { xs: 'none', md: 'flex' },
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			minWidth: 260,
			px: 3,
			textAlign: 'center',
		}}
	>
		<Box
			component="img"
			src="/assets/octopus_dark.png"
			alt="Randol"
			sx={{
				width: 180,
				height: 180,
				objectFit: 'contain',
				opacity: 0.96,
				filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.45))',
			}}
		/>
		<Typography variant="h4" fontWeight={700} sx={{ mt: 2 }}>
			Randol
		</Typography>
	</Box>
);

export default function RegistrationView() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const auth = useContext(AuthContext);
	const registrationClient = useApiClient(RegistrationClient);
	const invitationToken = searchParams.get('invitation') || undefined;
	const invitedEmail = searchParams.get('email') || '';
	const socialMode = searchParams.get('mode') === 'social';

	const [email, setEmail] = useState(invitedEmail);
	const [password, setPassword] = useState('');
	const [givenName, setGivenName] = useState('');
	const [surname, setSurname] = useState('');
	const [tenantName, setTenantName] = useState(searchParams.get('tenantName') || '');
	const [tenantCode, setTenantCode] = useState(searchParams.get('tenantCode') || '');
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [registeredTenantCode, setRegisteredTenantCode] = useState<string | null>(null);

	const isInvitation = useMemo(() => Boolean(invitationToken), [invitationToken]);
	const isCompletingSocial = socialMode && auth.isAuthenticated;
	const hasOrganizationName = isInvitation || Boolean(tenantName.trim());
	const hasLocalAccountRequiredFields =
		hasOrganizationName && Boolean(email.trim()) && Boolean(givenName.trim()) && Boolean(surname.trim()) && password.length >= 8;
	const canSubmit = isCompletingSocial ? hasOrganizationName : hasLocalAccountRequiredFields;

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!canSubmit) {
			return;
		}

		setError(null);
		setIsSaving(true);

		try {
			const result = isCompletingSocial
				? await registrationClient.completeSocialRegistration({
						tenantName: isInvitation ? undefined : tenantName,
						tenantCode: isInvitation ? undefined : tenantCode,
						invitationToken,
					})
				: await registrationClient.register({
						email,
						password,
						givenName,
						surname,
						displayName: `${givenName} ${surname}`.trim(),
						tenantName: isInvitation ? undefined : tenantName,
						tenantCode: isInvitation ? undefined : tenantCode,
						invitationToken,
					});
			setRegisteredTenantCode(result.tenantCode);
		} catch (e) {
			if (ApiException.isApiException(e)) {
				setError(e.response || e.message);
			} else {
				setError('Registration failed.');
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleSocialSignIn = async (provider: 'google' | 'facebook') => {
		setError(null);
		if (!isInvitation && !tenantName.trim()) {
			setError('Organization name is required before continuing with social sign-in.');
			return;
		}

		const params = new URLSearchParams();
		params.set('mode', 'social');
		params.set('provider', provider);
		if (isInvitation) {
			params.set('invitation', invitationToken!);
			if (email) {
				params.set('email', email);
			}
		} else {
			params.set('tenantName', tenantName);
			if (tenantCode) {
				params.set('tenantCode', tenantCode);
			}
		}

		await auth.authService.signInWithProvider(provider, `/register?${params.toString()}`);
	};

	const renderSocialIcon = (provider: 'google' | 'facebook') => (
		<Box
			component="img"
			src={socialProviderIcons[provider]}
			alt=""
			aria-hidden="true"
			sx={{
				width: 20,
				height: 20,
				objectFit: 'contain',
			}}
		/>
	);

	if (registeredTenantCode) {
		return (
			<Box sx={pageSx}>
				<Box sx={{ maxWidth: 560, mx: 'auto', p: 4, width: '100%' }}>
					<Paper sx={{ p: 3 }} variant="outlined">
						<Stack spacing={2}>
							<Typography variant="h5">Account created</Typography>
							<Typography color="text.secondary">Your Randol account is ready for organization {registeredTenantCode}. Sign in to continue.</Typography>
							<Button variant="contained" onClick={() => navigate('/home')}>
								Continue
							</Button>
						</Stack>
					</Paper>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={pageSx}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 4,
					maxWidth: 980,
					minHeight: '100vh',
					mx: 'auto',
					p: 4,
					width: '100%',
				}}
			>
				<BrandPanel />
				<Box sx={{ maxWidth: 640, width: '100%' }}>
					<Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }} variant="outlined">
						<Stack spacing={2.5}>
							<Box>
								<Typography variant="h5">{isInvitation ? 'Create your Randol account' : 'Register for Randol'}</Typography>
								<Typography color="text.secondary" sx={{ mt: 0.5 }}>
									{isInvitation ? 'Use the invited email address to join the organization.' : 'Create the first user and organization.'}
								</Typography>
							</Box>

							{error && <Alert severity="error">{error}</Alert>}

							{!isInvitation && (
								<>
									<TextField label="Organization Name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} required />
									<TextField
										label="Organization Code"
										value={tenantCode}
										onChange={(e) => setTenantCode(e.target.value)}
										helperText="Leave blank to generate a unique code from the organization name."
									/>
								</>
							)}

							{isCompletingSocial ? (
								<Alert severity="info">Continue to finish organization setup for {auth.account?.username || 'your social account'}.</Alert>
							) : (
								<>
									<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
										<Button variant="outlined" startIcon={renderSocialIcon('google')} onClick={() => handleSocialSignIn('google')} fullWidth>
											Continue with Google
										</Button>
										<Button variant="outlined" startIcon={renderSocialIcon('facebook')} onClick={() => handleSocialSignIn('facebook')} fullWidth>
											Continue with Facebook
										</Button>
									</Stack>
									<Divider>or</Divider>
									<TextField
										label="Email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
										type="email"
										disabled={isInvitation && !!invitedEmail}
									/>
									<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
										<TextField
											label="First name"
											name="randol-registration-given"
											value={givenName}
											onChange={(e) => setGivenName(e.target.value)}
											autoComplete="new-password"
											required
											fullWidth
										/>
										<TextField
											label="Last name"
											name="randol-registration-surname"
											value={surname}
											onChange={(e) => setSurname(e.target.value)}
											autoComplete="new-password"
											required
											fullWidth
										/>
									</Stack>
									<TextField
										label="Password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
										type="password"
										inputProps={{ minLength: 8 }}
									/>
								</>
							)}

							<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Button type="submit" variant="contained" disabled={isSaving || !canSubmit}>
									{isCompletingSocial ? 'Finish setup' : 'Create account'}
								</Button>
							</Box>
						</Stack>
					</Paper>
				</Box>
			</Box>
		</Box>
	);
}
